import type { ItemPhotoRow, ItemPhotoVariant } from 'helpers/itemPhotos';

export type PhotoSlot = { label: string; w: number; h: number; ratio: string };

// All slots share h=56px; width derives from each ratio (4:3→75, 1:1→56, 5:4→70, 3:2→84, 16:9→100).
// ChowNow is always shown; others appear when the integration is active.
// Integrations sharing a ratio (ChowNow + GrubHub at 4:3) are grouped into one slot.
const INTEGRATION_SIZES: Record<string, { label: string; w: number; h: number; ratio: string }> = {
  chownow: { label: 'ChowNow', w: 75, h: 56, ratio: '4:3' },
  doordash: { label: 'DoorDash', w: 56, h: 56, ratio: '1:1' },
  ubereats: { label: 'UberEats', w: 70, h: 56, ratio: '5:4' },
  grubhub: { label: 'GrubHub', w: 75, h: 56, ratio: '4:3' },
  skip: { label: 'SkipTheDishes', w: 84, h: 56, ratio: '3:2' },
  ritual: { label: 'Ritual', w: 100, h: 56, ratio: '16:9' },
};

export function buildPhotoSlots(activeIntegrationNames: Set<string>): PhotoSlot[] {
  const slotsBySize: Record<string, PhotoSlot> = {};

  Object.entries(INTEGRATION_SIZES).forEach(([key, config]) => {
    if (key !== 'chownow' && !activeIntegrationNames.has(key)) return;
    const sizeKey = `${config.w}x${config.h}`;
    if (slotsBySize[sizeKey]) {
      slotsBySize[sizeKey].label = `${slotsBySize[sizeKey].label}, ${config.label}`;
    } else {
      slotsBySize[sizeKey] = { label: config.label, w: config.w, h: config.h, ratio: config.ratio };
    }
  });

  return Object.values(slotsBySize).sort((a, b) => a.w - b.w);
}

/* Merges an image-endpoint response row into the list: only imageUrl/photos change.
The list's `enabled` is menu-scoped while the image endpoints aggregate it
merchant-wide, so the incoming `enabled` is deliberately dropped. */
export function mergeItemPhotoUpdate(
  list: ItemPhotoRow[] | null,
  updated: ItemPhotoRow | null | undefined,
): ItemPhotoRow[] | null {
  // Null means not loaded yet - keep it null so the loading overlay stays up.
  if (!list) return null;
  // Rows are matched by uuid, so without one there's no row to update.
  if (!updated?.uuid) return list;
  return list.map(row => {
    if (row.uuid !== updated.uuid) return row;
    return { ...row, imageUrl: updated.imageUrl, photos: updated.photos };
  });
}

/* The pre-cropped variant matching the slot's aspect ratio, or null when the BE
doesn't generate that ratio (the caller falls back to CSS-cropping the primary image). */
export function getSlotPhoto(
  photos: ItemPhotoVariant[] | undefined,
  slot: PhotoSlot,
): ItemPhotoVariant | null {
  return photos?.find(photo => photo.aspectRatio === slot.ratio) ?? null;
}
